import AppActivityIndicator from "@toothfairy/shared-ui/AppActivityIndicator";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyModal from "../../../../Components/ToothFairyComponents/ToothFairyModal";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";
import PromptPreSelection from "./SubComponents/PromptPreSelection";
import HiringTemplatesAPI from "../../../../API/HiringTemplates";
import Prompts from "../../../../API/Prompts";
import { showSideMenu } from "../../../../../App";

const GeneratorConfigList = ({ isDictionariesLoading }) => {
  const { currentTheme } = AppTheme.useTheme();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const { getActiveWorkspace, getPlanConfig } = useWorkspaces();
  const [showChoice, setShowChoice] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [selectedPromptId, setSelectedPromptId] = React.useState(null);
  const [displayedPrompts, setDisplayedPrompts] = React.useState([]);
  const [selectedPromptIds, setSelectedPromptIds] = React.useState([]);
  const [showBulkDeleteChoice, setShowBulkDeleteChoice] = React.useState(false);
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { height, width } = useWindowDimensions();
  const [isLoading, setIsLoading] = useState(true);
  const [showPreSelection, setShowPreSelection] = useState(false);
  const history = useHistory();
  const params = useParams();
  useEffect(() => {
    const optId = params?.optId;
    if (!optId) return;
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    if (isLoading) return;
    history.replace(`/workspaces/${wsId}/prompts/${optId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.optId, isLoading, getActiveWorkspace()?.id]);
  const columns = [
    {
      title: t("settings.list.id"),
      width: 0.00001,
    },
    {
      title: t("settings.list.name"),
      width: 0.3,
    },
    // {
    //   title: "Prompt length",
    // },

    {
      title: t("settings.list.prompt"),
    },
    // {
    //   title: "Prompt length",
    //   width: 0.1,
    // },
    {
      title: t("settings.list.actions"),
      isAction: true,
    },
  ];

  useEffect(() => {
    if (state?.customPrompts) {
      setIsLoading(true);
      setDisplayedPrompts(state?.customPrompts);
      setIsLoading(false);
    }
  }, [JSON.stringify(state?.customPrompts)]);

  const handlePromptItemSelection = ({ item, index }) => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId || !item?.id) return;
    history.push(`/workspaces/${wsId}/prompts/${item.id}`);
  };
  const handlePromptItemDeletion = async (item, index) => {
    try {
      setShowChoice(false);
      const _id = selectedPromptId || selectedItem?.id;
      if (_id) {
        const _deletedPrompt = await Prompts.deletePrompt(_id);
        dispatch(
          "SET_CUSTOM_PROMPTS",
          state?.customPrompts?.filter(
            (item) => item.id !== _deletedPrompt?.data?.deleteCustomPrompt?.id
          )
        );

        AppToasty.show(t("prompts.promptDeletionSuccessCaption"), {
          type: "success",
        });
        setSelectedIndex(null);
        setSelectedPromptId(null);
        setSelectedItem(null);
      } else {
        throw new Error("No item selected");
      }
    } catch (error) {
      console.log("error deleting prompt", error);
      AppToasty.show(t("prompts.promptDeletionFailedCaption"), {
        type: "danger",
      });
    }
  };
  const handleDuplication = async (id) => {
    try {
      let item = state?.customPrompts?.find((u) => u.id === id);
      if (!item) {
        AppToasty.show(t("prompts.promptDuplicationErrorCaption"), {
          type: "danger",
        });
        return;
      }
      let newPrompt = {
        ...item,
        label: `${item.label} - Copy`,
        workspaceID: getActiveWorkspace()?.id,
      };
      // Remove system fields
      delete newPrompt.id;
      delete newPrompt.createdAt;
      delete newPrompt.updatedAt;
      delete newPrompt._version;
      delete newPrompt._deleted;
      delete newPrompt._lastChangedAt;
      delete newPrompt.__typename;

      const result = await Prompts.createPrompt(newPrompt);
      if (result?.id) {
        // Reload the prompts list
        const promptsData = await Prompts.getCustomPromptsByWorkspaceID(
          getActiveWorkspace()?.id
        );
        const _prompts =
          promptsData?.data?.customPromptsByWorkspace?.items?.filter(
            (u) => u?._deleted != true
          ) || [];
        dispatch("SET_CUSTOM_PROMPTS", _prompts);
        AppToasty.show(t("promptDuplicationSuccessCaption"), {
          type: "success",
        });
      }
    } catch (error) {
      console.log(error);
      AppToasty.show(t("promptDuplicationErrorCaption"), {
        type: "danger",
      });
    }
  };
  const handleCreation = () => {
    setShowPreSelection(true);
  };

  const handleNewPromptSelect = () => {
    setShowPreSelection(false);
    history.push(`/workspaces/${getActiveWorkspace()?.id}/prompts/new`, {
      promptConfig: {
        label: "",
        interpolationString: "",
        promptLength: 256,
      },
    });
  };

  const handleTemplatePromptSelect = async (prompt) => {
    setShowPreSelection(false);
    await populateFormFromTemplatePrompt(prompt);
  };

  const populateFormFromTemplatePrompt = async (prompt) => {
    const promptData = await HiringTemplatesAPI.fetchCompletePromptData(
      prompt.id
    );

    if (!promptData) {
      AppToasty.show(t("settings.list.couldNotLoadToolData"), {
        type: "danger",
      });
      return;
    }

    let parsedPrompt = { ...promptData };
    parsedPrompt.id = undefined;
    parsedPrompt.workspaceID = getActiveWorkspace()?.id;
    parsedPrompt.parentPromptID = prompt.id;
    parsedPrompt.availableToAgents = [];

    history.push(`/workspaces/${getActiveWorkspace()?.id}/prompts/new`, {
      promptConfig: parsedPrompt,
    });
  };

  const handleBulkDeletion = async () => {
    try {
      for (const id of selectedPromptIds) {
        await Prompts.deletePrompt(id);
      }
      dispatch(
        "SET_CUSTOM_PROMPTS",
        state?.customPrompts?.filter(
          (item) => !selectedPromptIds.includes(item.id)
        )
      );
      setSelectedPromptIds([]);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("prompts.promptDeletionSuccessCaption"), {
        type: "success",
      });
    } catch (error) {
      console.log("error deleting prompts", error);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("prompts.promptDeletionFailedCaption"), {
        type: "danger",
      });
    }
  };
  const _coreChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancel")}
        caption={t("settings.list.deletePromptConfirmation")}
        subCaption={t("settings.list.deletePromptConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={() => handlePromptItemDeletion()}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    );
  }, [setShowChoice, showChoice, selectedItem, setSelectedItem, selectedIndex]);

  const _bulkDeleteChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancelConfirmation")}
        caption={t("settings.list.deleteSelectedConfirmation", {
          defaultValue: t("settings.list.deletePromptConfirmation"),
        })}
        subCaption={t("settings.list.deletePromptConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowBulkDeleteChoice(false)}
        onConfirmButtonPress={handleBulkDeletion}
        setShowChoice={setShowBulkDeleteChoice}
        showChoice={showBulkDeleteChoice}
      />
    );
  }, [showBulkDeleteChoice, selectedPromptIds]);

  return (
    <View
      style={{
        paddingLeft: 10,
        minWidth: "100%",
        flex: 1,
        width: width - (showSideMenu() ? 220 : 0),
      }}
    >
      {isLoading ? (
        <ToothFairyModal
          backgroundColor={currentTheme?.theme?.softBackgroundColor}
          visible={true}
          containerStyle={{
            backgroundColor: currentTheme?.theme?.transparent,
            minHeight: 250,
          }}
          caption={t("settings.list.loadingPrompts")}
        />
      ) : (
        <>
          {displayedPrompts.length > 0 ? (
            <AppTable
              isSelectable
              showPagination
              itemsPerPage={50}
              hideSearch={false}
              searchPlaceholder={t("settings.toolbar.searchByNameOrPrompt")}
              onSelectionChange={(selectedItems) => {
                setSelectedPromptIds(selectedItems?.map((u) => u?.id) || []);
              }}
              bulkActions={[
                {
                  id: "DELETE",
                  label: t("settings.list.delete"),
                  icon: AppAssets.icons.delete,
                  isDestructive: true,
                },
              ]}
              onBulkActionPress={(id) => {
                if (id === "DELETE" && selectedPromptIds?.length > 0)
                  setShowBulkDeleteChoice(true);
              }}
              actionButtons
              showDropDownMenuForActions
              mainActionLabel={t("settings.list.edit")}
              onClickMainAction={(item, index) => {
                handlePromptItemSelection({ item, index });
              }}
              onActionItemPress={(id, item, index) => {
                if (id === "EDIT") {
                  handlePromptItemSelection({ item, index });
                } else if (id === "DUPLICATE") {
                  handleDuplication(item?.id);
                } else if (id === "DELETE") {
                  setSelectedItem(item);
                  setSelectedIndex(index);
                  setSelectedPromptId(item?.id);
                  setShowChoice(true);
                }
              }}
              actions={[
                {
                  label: t("settings.list.edit"),
                  id: "EDIT",
                  icon: AppAssets.icons.edit,
                },
                {
                  label: t("settings.list.duplicate"),
                  id: "DUPLICATE",
                  icon: AppAssets.icons.duplicate,
                },
                {
                  label: t("settings.list.delete"),
                  id: "DELETE",
                  icon: AppAssets.icons.delete,
                },
              ]}
              style={{
                zIndex: -1,
                marginTop: 15,
                height:
                  displayedPrompts?.length >= 50 ? height - 95 : height - 75,
                paddingRight: 0,
                overflowY: "auto",
                overflowX: "hidden",
              }}
              headers={columns}
              items={displayedPrompts
                ?.sort((a, b) => (a?.label || "").localeCompare(b?.label || ""))
                ?.map((u) => {
                  return {
                    id: u?.id || " ",
                    name: u?.label || " ",
                    prompt: u?.interpolationString || " ",
                    actions: " ",
                  };
                })}
            >
              <ToothFairyButton
                title={t("common.create")}
                onPress={handleCreation}
              />
            </AppTable>
          ) : (
            <AppPlaceholderView
              wrapperStyle={{
                width: "100%",
                height: "100%",
                marginVertical: 20,
                marginHorizontal: "auto",
                flex: 1,
              }}
              size={30}
              icon={AppAssets.icons.writer}
              mainCaption={t("settings.list.noPromptsFound")}
              subCaption={t("settings.list.noPromptsFoundSub")}
              actionLabel={t("common.create")}
              onActionPress={handleCreation}
            />
          )}
        </>
      )}
      {_coreChoice}
      {_bulkDeleteChoice}
      {showPreSelection && (
        <PromptPreSelection
          onNewPromptSelect={handleNewPromptSelect}
          onTemplatePromptSelect={handleTemplatePromptSelect}
          onClose={() => setShowPreSelection(false)}
        />
      )}
    </View>
  );
};

export default GeneratorConfigList;
